import { useRef } from "react";

import { observer } from "mobx-react-lite";

import { drawPath } from "@/components/expression/svgUtils/drawPath";
import { useNodePoints } from "@/components/expression/svgUtils/useNodePoints";
import { useNodeThemeColors } from "@/components/expression/theme/useNodeTheme";
import { useNodeStyle } from "@/components/expression/utils/NodeStyleProvider";
import { twMerge } from "@/utils/twMerge";

import type { DecoratorNodeModel } from "@/models/nodes/DecoratorNodeModel";
import type { NodeSize } from "@corca/data";

const VECTOR_HEIGHT_MAP: Record<NodeSize, number> = {
  sm: 6,
  xs: 5,
  xxs: 4,
  xxxs: 3,
};

const VECTOR_STROKE_WIDTH_MAP: Record<NodeSize, number> = {
  sm: 1,
  xs: 1,
  xxs: 0.5,
  xxxs: 0.5,
};

type Props = {
  node: DecoratorNodeModel;
  position?: "over" | "under";
  direction?: "right" | "left" | "both";
};

export const Vector = observer(function Vector({
  node,
  position = "over",
  direction = "right",
}: Props) {
  const { parent: symbolNode } = node;

  const containerRef = useRef<HTMLDivElement>(null);
  const { nodePoints, viewBox } = useNodePoints(containerRef, node);
  const { view, size } = useNodeStyle();
  const theme = useNodeThemeColors(symbolNode?.parent);

  const isTextView = view === "text";
  const smallerSize = size === "xs" || size === "xxs" || size === "xxxs";

  const height = VECTOR_HEIGHT_MAP[size];
  const strokeWidth = isTextView ? VECTOR_STROKE_WIDTH_MAP[size] : VECTOR_STROKE_WIDTH_MAP.sm;

  let path = drawPath()
    .moveTo(nodePoints.lc)
    .lineTo(nodePoints.rc)
    .lineTo({
      x: nodePoints.rc.x - height / 2.5,
      y: nodePoints.rc.y - height / 2.5,
    })
    .moveTo(nodePoints.rc)
    .lineTo({
      x: nodePoints.rc.x - height / 2.5,
      y: nodePoints.rc.y + height / 2.5,
    })
    .finish();

  if (direction === "both") {
    path = drawPath(0, 0, path)
      .moveTo(nodePoints.lc)
      .lineTo({
        x: nodePoints.lc.x + height / 2.5,
        y: nodePoints.lc.y - height / 2.5,
      })
      .moveTo(nodePoints.lc)
      .lineTo({
        x: nodePoints.lc.x + height / 2.5,
        y: nodePoints.lc.y + height / 2.5,
      })
      .finish();
  }

  return (
    <div
      className={twMerge(
        "absolute -left-[1px] -right-[1px] h-[6px]",

        position === "over" && "-top-[0.15rem]",
        position === "under" && "-bottom-[0.15rem]",

        direction === "left" && "rotate-180",

        (isTextView || smallerSize) && "left-0 right-0",

        isTextView && size === "xxs" && "-top-[0.08rem]",
        isTextView && size === "xxxs" && "-top-[0.2rem]",
      )}
    >
      <div ref={containerRef} className="w-full h-full relative">
        <svg
          className="absolute left-0 top-0 h-full w-full"
          fill="none"
          preserveAspectRatio="none"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
          width={viewBox.width}
          height={viewBox.height}
        >
          <path
            d={path}
            className={twMerge(
              "stroke-document-text",
              theme?.symbol && `stroke-${theme.symbol.light} dark:stroke-${theme.symbol.dark}`,
            )}
          />
        </svg>
      </div>
    </div>
  );
});
